<script setup lang="ts">
import { Printer } from '@capgo/capacitor-printer'

interface ListItem {
  id: number
  label: string
  qty: number
  slashed: number
  flyer_item?: {
    price_description: string
  }
}

interface List {
  id: number
  label: string
  items: ListItem[]
}

interface Props {
  lists: List[]
}

const props = defineProps<Props>()
const baseStore = useBaseStore()

function getPrintFooterText() {
  const formattedDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return `Généré sur Glouton le ${formattedDate}`
}

// Fonction pour imprimer avec Capacitor (iOS/Android)
async function appPrint() {
  try {
    const htmlContent = generatePrintHTML()
    await Printer.printHtml({
      name: 'Ma liste Glouton',
      html: htmlContent,
    })
  }
  catch (error) {
    console.error('Printing error: ', error)
  }
}

// Fonction pour imprimer
async function printList() {
  // Utiliser Capacitor Printer pour iOS et Android
  if (baseStore.platform === 'ios' || baseStore.platform === 'android') {
    await appPrint()
    return
  }

  // Fallback pour le web
  const printWindow = window.open('', '_blank')

  if (printWindow) {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liste d'épicerie - Glouton</title>
        <style>
          @page {
            margin: 0.35in 0.35in 0.65in;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 5px;
            font-size: 14px;
            line-height: 1.4;
          }
          .list-container {
            margin-bottom: 10px;
          }
          .list-header {
            font-weight: bold;
            font-size: 16px;
            margin-bottom: 5px;
            padding: 2px;
            background-color: #f5f5f5;
            border-bottom: 2px solid #ddd;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .list-item {
            padding: 3px 0;
            border-bottom: 1px solid #eee;
            display: flex;
            align-items: center;
          }
          .checkbox {
            width: 10px;
            height: 10px;
            border: 1px solid #333;
            margin-right: 15px;
            display: inline-block;
            position: relative;
          }
          .item-content {
            flex: 1;
          }
          .item-quantity {
            font-weight: bold;
            margin-right: 8px;
          }
          .item-label {
            font-size: 16px;
          }
          .item-price {
            color: #666;
            font-size: 12px;
            margin-left: 8px;
            font-style: italic;
          }
          .line-through {
            text-decoration: line-through;
            color: #999;
          }
          .print-footer {
            position: fixed;
            right: 0;
            bottom: 0;
            left: 0;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          @media print {
            body { margin: 15px; }
            .list-container {  }
            .checkbox { 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <h1 style="text-align: center; margin-bottom: 10px; color: #333;">Liste d'épicerie - Glouton</h1>
        ${generateListHTML()}
        <div class="print-footer">${getPrintFooterText()}</div>
      </body>
      </html>
    `)

    printWindow.document.close()

    printWindow.onload = () => {
      printWindow.print()
      printWindow.close()
    }
  }
  else {
    // Fallback si popup bloqué
    window.print()
  }
}

// Fonction pour générer le HTML complet pour l'impression
function generatePrintHTML() {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Liste d'épicerie - Glouton</title>
      <meta charset="UTF-8">
      <style>
        @page {
          margin: 0.5in 0.5in 0.65in;
          size: Letter;
        }
        
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 10px;
          font-size: 12px;
          line-height: 1.2;
          color: #333;
        }
        
        .print-header {
          text-align: center;
          margin-bottom: 15px;
          border-bottom: 1px solid #F37A4E;
          padding-bottom: 8px;
        }
        
        .print-title {
          font-size: 18px;
          font-weight: bold;
          color: #F37A4E;
          margin: 0 0 3px 0;
        }
        
        .print-date {
          font-size: 12px;
          color: #666;
          margin: 0;
        }
        
        .list-container {
          margin-bottom: 12px;
        }
        
        .list-header {
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 4px;
          padding: 4px 8px;
          background-color: #f5f5f5;
          border-left: 3px solid #F37A4E;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #333;
        }
        
        .list-item {
          padding: 3px 0;
          border-bottom: 1px solid #eee;
          display: flex;
          align-items: center;
          min-height: 16px;
        }
        
        .checkbox {
          width: 10px;
          height: 10px;
          border: 1px solid #333;
          margin-right: 10px;
          display: inline-block;
          position: relative;
          flex-shrink: 0;
        }
        
        .item-content {
          flex: 1;
          display: flex;
          align-items: center;
        }
        
        .item-quantity {
          font-weight: bold;
          margin-right: 6px;
          color: #F37A4E;
          font-size: 11px;
        }
        
        .item-label {
          font-size: 13px;
          flex: 1;
        }
        
        .item-price {
          color: #666;
          font-size: 10px;
          margin-left: 6px;
          font-style: italic;
        }
        
        .line-through {
          text-decoration: line-through;
          color: #999;
        }
        
        .print-footer {
          position: fixed;
          right: 0;
          bottom: 0;
          left: 0;
          text-align: center;
          font-size: 10px;
          color: #666;
        }
        
        @media print {
          body { 
            margin: 0;
            padding: 8px;
            font-size: 11px;
          }
          .list-container { 
          }
          .checkbox { 
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-header {
            margin-bottom: 10px;
          }
          .list-item {
            padding: 2px 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <h1 class="print-title">Liste d'épicerie</h1>
      </div>
      
      ${generateListHTML()}
      
      <div class="print-footer">
        ${getPrintFooterText()}
      </div>
    </body>
    </html>
  `
}

// Fonction pour générer le HTML des listes
function generateListHTML() {
  let html = ''

  props.lists.forEach((list) => {
    if (list.items.length > 0) {
      html += `
        <div class="list-container">
          <div class="list-header">${list.label}</div>
      `

      list.items.forEach((item) => {
        const quantity = item.qty > 1 ? `${item.qty}x ` : ''
        const price = item.flyer_item ? ` (${item.flyer_item.price_description})` : ''
        const lineThrough = item.slashed === 1 ? 'line-through' : ''

        html += `
          <div class="list-item">
            <div class="checkbox"></div>
            <div class="item-content">
              <span class="item-quantity">${quantity}</span>
              <span class="item-label ${lineThrough}">${item.label}</span>
              <span class="item-price">${price}</span>
            </div>
          </div>
        `
      })

      html += `</div>`
    }
  })

  return html
}

// Exposer la fonction d'impression
defineExpose({
  printList,
})
</script>

<template>
  <div style="display: none;">
    <!-- Ce composant n'a pas de template visible, il sert uniquement pour l'impression -->
  </div>
</template>
